import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import { Button, Icon, Modal } from 'design-system-web';

import { routes } from 'lane-shared/config';
import { Flex, CreatedBy, Loading, Tooltip } from 'components';
import { AdminPage, PageHeader } from 'components/layout';
import { ButtonType } from 'components/layout/PageHeader';
import useChannelAdminContext from 'hooks/useChannelAdminContext';
import { H4, S } from 'components/typography';

import styles from './styles.scss';
import { convertTo62 } from 'uuid-encoding';
import { OFFICE_TYPES, RETAIL_TYPES } from 'constants-channel';
import { hasPermission } from 'lane-shared/helpers';
import { hasPermissionOrSuperUser } from 'lane-shared/helpers/hasPermission';
import { PERMISSION_KEYS } from 'constants-permissions';
import { UserDataContext } from 'lane-shared/contexts';
import { ICON_SET_FONTAWESOME } from 'lane-shared/helpers/constants/icons';
import { useFlag } from 'lane-shared/hooks';
import { FeatureFlag } from 'constants-flags';

import { useFetchTenant } from './hooks/useFetchTenant';
import { useCreateTenant } from './hooks/useCreateTenant';
import { useRemoveTenantRelationship } from '../hooks/useRemoveTenantRelationship';
import { TenantLeasesSection } from './components/TenantLeasesSection';
import { TenantPermittedDomains } from './components/TenantPermittedDomains/TenantPermittedDomains';

type ChannelTypeValue =
  | (typeof OFFICE_TYPES)[number]
  | (typeof RETAIL_TYPES)[number];
type TenantChannelNav = {
  slug: string;
  type: ChannelTypeValue;
};

export function TenantDetails() {
  const { t } = useTranslation();
  const history = useHistory();
  const { channel } = useChannelAdminContext();
  const { user } = useContext(UserDataContext);
  const [isNotFoundModalOpen, setIsOpenNotFoundModal] = useState(false);
  const { tenantId } = useParams<{ tenantId: string }>();
  const { data, error } = useFetchTenant(channel?._id, tenantId);

  const tenant = data?.tenant;
  const tenantChannelDetails = tenant?.channelDetails;

  const { removeTenant, isLoading: isRemovingTenant } =
    useRemoveTenantRelationship();

  const isTenantDomainWhitelistingEnabled = useFlag(
    FeatureFlag.EnableTenantDomainWhitelisting,
    false
  );

  useCreateTenant(tenant?._id, channel?._id);

  useEffect(() => {
    if ((data && !tenant && !error) || (!data && error)) {
      setIsOpenNotFoundModal(true);
    }
  }, [tenant, error, data, t]);

  if (isRemovingTenant) {
    return <Loading fullscreen />;
  }

  function goToChannel(rel: TenantChannelNav) {
    const hasRetailAdmin =
      RETAIL_TYPES.includes(rel.type) &&
      hasPermission(channel?.roles, [
        PERMISSION_KEYS.PERMISSION_ADMIN,
        PERMISSION_KEYS.PERMISSION_PROPERTY_MANAGE_RETAIL,
      ]);
    const hasOfficeAdmin =
      OFFICE_TYPES.includes(rel.type) &&
      hasPermission(channel?.roles, [
        PERMISSION_KEYS.PERMISSION_ADMIN,
        PERMISSION_KEYS.PERMISSION_PROPERTY_MANAGE_COMPANY,
      ]);

    let route: string;

    if (user?.isSuperUser || hasRetailAdmin || hasOfficeAdmin) {
      route = routes.channelAdmin.replace(':id', rel.slug);
    } else {
      route = routes.channel.replace(':id', rel.slug);
    }

    history.push(route);
  }

  const userHasViewEditLeasesPermission = () => {
    if (!user || !channel) {
      return false;
    }

    return hasPermissionOrSuperUser(
      user,
      [PERMISSION_KEYS.PERMISSION_ADMIN],
      channel?._id
    );
  };

  const handleRemoveTenant = async () => {
    if (!tenant?.id || !tenant?.name) {
      return;
    }

    const relationshipId = tenant.relationships?.[0]?.id
      ? convertTo62(tenant.relationships[0].id)
      : '';

    const isRemoved = await removeTenant({
      relationshipId,
      tenantName: tenant.name,
    });

    if (isRemoved && channel?.slug) {
      const tenantListUrl = routes.channelAdminRelationships.replace(
        ':id',
        channel.slug
      );

      history.push(tenantListUrl);
    }
  };

  const actionButtons = [
    {
      label: t(
        'web.admin.channel.settings.tenantManagement.tenant.details.removeTenant'
      ),
      type: 'secondary' as ButtonType,
      onClick: async () => handleRemoveTenant(),
      testId: 'remove-tenant',
    },
    {
      label: `${t(
        'web.admin.channel.settings.tenantManagement.tenant.details.goto'
      )} ${tenant?.name}`,
      type: 'primary' as ButtonType,
      onClick: () => goToChannel(tenant.channelDetails),
      testId: 'goto-tenant',
    },
  ];

  const tenantAddress = (
    <span className="tenant-location">
      <S className="block">{tenantChannelDetails?.address?.street1}</S>
      <S className="block">{tenantChannelDetails?.address?.street2}</S>
      <S>
        {[
          tenantChannelDetails?.address?.city,
          tenantChannelDetails?.address?.state,
          tenantChannelDetails?.address?.code,
        ]
          .filter(Boolean)
          .join(', ')}
      </S>
    </span>
  );

  const pageHeader = (
    <PageHeader
      header={tenant?.name}
      headerLevel="h3"
      actionButtons={actionButtons}
      externalPadding={[0, 0]}
      breadcrumbs={[
        {
          label: t('web.admin.channel.settings.tenantManagement.name'),
          url: routes.channelAdminRelationships.replace(
            ':id',
            channel?.slug || ''
          ),
        },
        {
          label: tenant?.name
            ? `${tenant?.name} ${t('web.admin.channel.settings.tenantManagement.tenant.details')}`
            : '',
        },
      ]}
      description={tenantAddress}
    />
  );

  if (!tenant || error) {
    return (
      <AdminPage>
        {pageHeader}
        <Modal
          isOpen={isNotFoundModalOpen}
          onClose={() => {
            setIsOpenNotFoundModal(false);
          }}
          title={t(
            'web.admin.channel.settings.tenantManagement.tenant.details.notFound.title'
          )}
          actions={
            <Button
              onClick={() => {
                setIsOpenNotFoundModal(false);
              }}
            >
              {t(
                'web.admin.channel.settings.tenantManagement.tenant.details.notFound.confirm'
              )}
            </Button>
          }
          testId="tenant-not-found"
        >
          {t(
            'web.admin.channel.settings.tenantManagement.tenant.details.notFound.message'
          )}
        </Modal>
      </AdminPage>
    );
  }

  const createdBy = {
    _createdBy: {
      _id: tenantChannelDetails.createdBy,
    },
    _updatedBy: {
      _id: tenantChannelDetails.updatedBy,
    },
    _created: tenantChannelDetails.created,
    _updated: tenantChannelDetails.updated,
  };

  const renderTenantLeasesSection = () => {
    const hasPermission = userHasViewEditLeasesPermission();

    if (!hasPermission) return null;

    return (
      <div className={styles.section}>
        <TenantLeasesSection
          tenantId={tenantId}
          propertyId={channel?._id || ''}
        />
      </div>
    );
  };

  return (
    <AdminPage>
      {pageHeader}

      <div className="mt-4">
        <div className={styles.section}>
          <Flex align="center" mb={4}>
            <H4>
              {t`web.admin.channel.settings.tenantManagement.tenant.details.overview`}
            </H4>
            <Tooltip
              TooltipComponent={
                <>{t`web.admin.channel.settings.tenantManagement.tenant.details.overview.tooltip`}</>
              }
              placement="right"
            >
              <Icon
                className="cursor-pointer"
                name="info-circle"
                type="far"
                set={ICON_SET_FONTAWESOME}
                size="medium"
              />
            </Tooltip>
          </Flex>
          <Flex direction="column">
            <Flex className={styles.rows}>
              <Flex className={styles.field}>
                <span>
                  {t(
                    'web.admin.channel.settings.tenantManagement.tenant.details.activeMembers'
                  )}{' '}
                </span>
                <span>{tenantChannelDetails?.stats?.subscribers}</span>
              </Flex>
              <Flex className={styles.field}>
                <span>
                  {t(
                    'web.admin.channel.settings.tenantManagement.tenant.details.type'
                  )}
                </span>
                <span>{tenantChannelDetails?.type}</span>
              </Flex>
              <Flex className={styles.field}>
                <span>
                  {t(
                    'web.admin.channel.settings.tenantManagement.tenant.details.squareFootage'
                  )}
                </span>
                <span>
                  {tenantChannelDetails?.stats?.sf}{' '}
                  {t(
                    'web.admin.channel.settings.tenantManagement.tenant.details.sqFt'
                  )}
                </span>
              </Flex>
            </Flex>
          </Flex>
        </div>
      </div>

      {isTenantDomainWhitelistingEnabled && (
        <div className="mt-4">
          <div className={styles.section}>
            <TenantPermittedDomains tenant={tenant}/>
          </div>
        </div>
      )}

      <div className="mt-2">{renderTenantLeasesSection()}</div>
      <div className={styles.createdByFooter}>
        <CreatedBy object={createdBy} forAdmin />
      </div>
    </AdminPage>
  );
}
